<script>
import { required, url } from 'vuelidate/lib/validators';
import moment from 'moment-timezone';
import ENUMS from '@shared/messages/enums.json';
import TIMEZONES from '@shared/constants/timezones';
import TiptapEditor from '@shared/components/TiptapEditor.vue';
import CommunityMembersImportForm from '@shared/components/CommunityMembersImportForm.vue';
import APICalendarEvents from '@app/services/API/CalendarEvents';
import APITrainings from '@app/services/API/Trainings';
import APIUpload from '@shared/services/API/Upload';

const FREQUENCY_OPTIONS = [
  { value: 'once', label: ENUMS.calendarEventFrequency.once },
  { value: 'daily', label: ENUMS.calendarEventFrequency.daily },
  { value: 'weekly', label: ENUMS.calendarEventFrequency.weekly },
  { value: 'monthly', label: ENUMS.calendarEventFrequency.monthly },
  { value: 'yearly', label: ENUMS.calendarEventFrequency.yearly },
  { value: 'first_of_month', label: ENUMS.calendarEventFrequency.first_of_month },
  { value: 'last_of_month', label: ENUMS.calendarEventFrequency.last_of_month },
];

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Brouillon' },
  { value: 'PUBLISHED', label: 'Publié' },
  { value: 'CANCELLED', label: 'Annulé' },
];

const LOCATION_OPTIONS = [
  {
    value: 'visio',
    label: 'Lien de visioconférence',
    icon: 'video',
    placeholder: 'Talkizy, Google Meet, Zoom, etc.',
  },
  {
    value: 'address',
    label: 'Adresse',
    icon: 'map-marker-alt',
    placeholder: 'Adresse de l\'événement',
  },
  {
    value: 'link',
    label: 'Lien',
    icon: 'external-link-alt',
    placeholder: 'https://...',
  },
];

const NO_LOCATION_OPTION = { value: null, label: 'Aucun lieu', icon: 'ban' };

export default {
  components: { TiptapEditor, CommunityMembersImportForm },
  props: {
    event: {
      type: Object,
      default: null,
    },
  },
  data() {
    const storeTimezone = this.$store.getters['auth/store']?.timezone || 'Europe/Paris';
    const browserTimezone = moment.tz.guess();
    const defaultTimezone = TIMEZONES.some((tz) => tz.value === browserTimezone)
      ? browserTimezone
      : storeTimezone;

    let form;
    if (this.event) {
      const tz = this.event.timezone || storeTimezone;
      const startMoment = moment.utc(this.event.start_date).tz(tz);
      const endMoment = this.event.end_date ? moment.utc(this.event.end_date).tz(tz) : null;
      const sameDay = !endMoment || startMoment.isSame(endMoment, 'day');

      form = {
        title: this.event.title,
        description: this.event.description || '',
        start_date: startMoment.toDate(),
        start_time: startMoment.format('HH:mm'),
        end_date: endMoment ? endMoment.toDate() : null,
        end_time: endMoment ? endMoment.format('HH:mm') : '',
        // Le modèle stocke start/end : la durée n'est qu'une vue du formulaire
        // (mode journée simple), reconvertie en end_date à l'enregistrement.
        duration: endMoment && sameDay && !this.event.is_all_day
          ? endMoment.diff(startMoment, 'minutes')
          : null,
        timezone: tz,
        // 'talkizy' n'est plus proposé dans le formulaire : repris comme visio
        location_type: this.event.location_type === 'talkizy' ? 'visio' : this.event.location_type,
        location_value: this.event.location_value || '',
        frequency: this.event.frequency || 'once',
        status: this.event.status || 'DRAFT',
        multiDay: !sameDay,
        allDay: !!this.event.is_all_day,
        picture: null,
        training_uuids: (this.event.trainings || []).map((t) => t.uuid),
      };
    } else {
      form = {
        title: '',
        description: '',
        start_date: null,
        // Prochaine heure ronde à +1 h / +2 h (ex. à 13h53 → 15:00),
        // plutôt qu'un 09:00 arbitraire.
        start_time: moment().startOf('hour').add(2, 'hours').format('HH:mm'),
        end_date: null,
        end_time: '',
        duration: null,
        timezone: defaultTimezone,
        location_type: null,
        location_value: '',
        frequency: 'once',
        status: 'DRAFT',
        multiDay: false,
        allDay: false,
        picture: null,
        training_uuids: [],
      };
    }

    return {
      isLoading: false,
      isUploading: false,
      form,
      pictureFile: null,
      trainings: [],
      picturePreview: this.event?.picture || null,
      FREQUENCY_OPTIONS,
      STATUS_OPTIONS,
      LOCATION_OPTIONS,
      NO_LOCATION_OPTION,
      showTrainingModal: false,
      openDescription: !!(this.event && this.event.description),
      openImage: !!(this.event && this.event.picture),
      openAccess: !!(this.event && (this.event.trainings || []).length),
    };
  },
  validations() {
    const rules = {
      form: {
        title: { required },
        start_date: { required },
        end_date: {},
        timezone: { required },
        frequency: { required },
        location_value: {},
      },
    };

    if (this.form.multiDay) {
      rules.form.end_date = { required };
    }

    if (this.form.location_type) {
      rules.form.location_value = { required };

      if (this.form.location_type !== 'address') {
        rules.form.location_value.url = url;
      }
    }

    return rules;
  },
  computed: {
    isEditing() {
      return !!this.event;
    },
    // Pas de date passée, en création comme en modification (aujourd'hui inclus).
    // L'heure du jour est contrôlée en plus par isStartInPast / startInPast.
    minStartDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    // « Annulé » n'a pas de sens pour un événement qui n'existe pas encore :
    // il n'est proposé qu'en modification.
    statusOptions() {
      if (this.isEditing) {
        return this.STATUS_OPTIONS;
      }
      return this.STATUS_OPTIONS.filter((opt) => opt.value !== 'CANCELLED');
    },
    currentLocationOption() {
      return this.LOCATION_OPTIONS.find((opt) => opt.value === this.form.location_type)
        || this.NO_LOCATION_OPTION;
    },
    locationErrorRules() {
      return [
        'required',
        { name: 'url', message: 'Le lieu doit être une URL valide' },
      ];
    },
    timezoneOptions() {
      let options = this.TIMEZONES_LIST;

      if (this.form.timezone && !options.some((tz) => tz.value === this.form.timezone)) {
        options = [{ value: this.form.timezone, label: this.form.timezone }, ...options];
      }

      // Les offsets des libellés sont recalculés à la date de l'événement :
      // les libellés statiques du fichier de constantes portent l'offset
      // d'hiver, faux la moitié de l'année pour les zones à heure d'été
      // (ex. Paris : UTC+1 en janvier, UTC+2 en juillet).
      const reference = this.form.start_date || new Date();

      return options.map((tz) => {
        const offset = moment.tz(reference, tz.value).format('Z');
        const city = tz.label.replace(/^\(UTC[+-]\d{2}:\d{2}\)\s*/, '');
        return { value: tz.value, label: `(UTC${offset}) ${city}` };
      });
    },
    TIMEZONES_LIST() {
      return TIMEZONES;
    },
    dateWarning() {
      if (!this.form.start_date) return null;
      const day = this.$moment(this.form.start_date).date();
      const freq = this.form.frequency;

      if (freq !== 'monthly' && freq !== 'yearly') return null;

      if (day === 29) {
        return 'Le 29 février n\'existe que les années bissextiles. Cet événement pourrait être ignoré certaines années.';
      }
      if (day === 30 && freq === 'yearly') {
        return 'Le 30 février n\'existe pas. Cet événement sera ignoré en février.';
      }
      if (day === 31) {
        return 'Le 31 n\'existe pas tous les mois (avril, juin, sept., nov.). Cet événement sera ignoré ces mois-là.';
      }
      return null;
    },
    trainingHelpText() {
      if (this.form.training_uuids.length > 0) {
        return 'Seuls les apprenants inscrits aux formations sélectionnées verront cet événement.';
      }
      return 'Cet événement sera visible par tous vos apprenants.';
    },
    // Réactif : sert à afficher l'alerte et à désactiver le bouton en direct.
    startInPast() {
      return this.isStartInPast();
    },
  },
  created() {
    this.fetchTrainings();
  },
  watch: {
    // Un événement sur plusieurs jours ne peut pas être récurrent : la fréquence
    // n'a plus de sens, on la ré-initialise (le champ est aussi grisé côté template).
    'form.multiDay': function(isMulti) {
      if (isMulti) {
        this.form.frequency = 'once';
      }
    },
    'form.timezone': function(newTz, oldTz) {
      if (!oldTz || !newTz || !this.form.start_date) return;

      const startDateStr = this.$moment(this.form.start_date).format('YYYY-MM-DD');
      const startInOld = moment.tz(`${startDateStr} ${this.form.start_time}:00`, oldTz);
      const startInNew = startInOld.clone().tz(newTz);
      this.form.start_time = startInNew.format('HH:mm');
      this.form.start_date = startInNew.toDate();

      if (this.form.end_date && this.form.end_time) {
        const endDateStr = this.$moment(this.form.end_date).format('YYYY-MM-DD');
        const endInOld = moment.tz(`${endDateStr} ${this.form.end_time}:00`, oldTz);
        const endInNew = endInOld.clone().tz(newTz);
        this.form.end_time = endInNew.format('HH:mm');
        this.form.end_date = endInNew.toDate();
      }
    },
  },
  methods: {
    // Un input[type=time] natif n'ouvre son sélecteur que via l'icône horloge ;
    // un clic sur les chiffres ne fait que sélectionner le segment. showPicker()
    // ouvre le sélecteur quel que soit l'endroit cliqué (no-op si non supporté).
    openTimePicker(event) {
      const input = event.target.tagName === 'INPUT'
        ? event.target
        : event.currentTarget.querySelector('input');

      if (input && typeof input.showPicker === 'function') {
        try {
          input.showPicker();
        } catch (e) {
          // showPicker peut refuser (geste utilisateur requis, iframe…) : la
          // saisie clavier reste possible.
        }
      }
    },
    async fetchTrainings() {
      try {
        const { data } = await APITrainings.getTrainings();
        this.trainings = data;
      } catch (e) {
        // silent
      }
    },
    handlePicture(file) {
      if (!file) {
        this.form.picture = null;
        this.picturePreview = null;
        return;
      }

      if (file.size > this.$constants.MAX_IMAGE_SIZE) {
        this.pictureFile = null;
        this.$buefy.dialog.alert(this.$t('messages.file_larger_than', {
          size: '5 Mo',
        }));
        return;
      }

      this.picturePreview = URL.createObjectURL(file);
      this.isUploading = true;

      APIUpload.uploadFileToS3({ type: 'image', file })
        .then((url) => {
          this.form.picture = url;
        })
        .catch(() => {
          this.form.picture = null;
          this.picturePreview = null;
          this.pictureFile = null;
          this.$buefy.toast.open({
            message: 'Erreur lors de l\'envoi de l\'image',
            type: 'is-danger',
          });
        })
        .finally(() => {
          this.isUploading = false;
        });
    },
    removePicture() {
      if (this.picturePreview && this.picturePreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.picturePreview);
      }
      this.form.picture = null;
      this.picturePreview = null;
      this.pictureFile = null;
    },
    getTrainingName(uuid) {
      const t = this.trainings.find((tr) => tr.uuid === uuid);
      return t ? this.trainingLabel(t) : uuid;
    },
    trainingLabel(training) {
      if (!training.status || training.status === 'PUBLISHED') {
        return training.name;
      }
      return `${training.name} (${(ENUMS.trainingStatus[training.status] || training.status).toLowerCase()})`;
    },
    applyTrainingSelection({ trainings }) {
      this.form.training_uuids = [...trainings];
      return Promise.resolve();
    },
    removeTraining(uuid) {
      const idx = this.form.training_uuids.indexOf(uuid);
      if (idx >= 0) {
        this.form.training_uuids.splice(idx, 1);
      }
    },
    // Un événement ne peut pas commencer dans le passé (création comme modification).
    // Journée entière : on tolère la journée en cours (fin de journée >= maintenant) ;
    // sinon on compare la date + l'heure de début dans le fuseau saisi. Recalculé à
    // l'appel (pas de cache) pour rester juste même après un long temps d'édition.
    isStartInPast() {
      if (!this.form.start_date) return false;

      const tz = this.form.timezone || 'UTC';
      const dateStr = this.$moment(this.form.start_date).format('YYYY-MM-DD');

      if (this.form.allDay) {
        const endDateStr = this.form.multiDay && this.form.end_date
          ? this.$moment(this.form.end_date).format('YYYY-MM-DD')
          : dateStr;
        return moment.tz(`${endDateStr} 23:59:59`, tz).isBefore(moment());
      }

      if (!this.form.start_time) {
        return moment.tz(`${dateStr} 23:59:59`, tz).isBefore(moment());
      }

      return moment.tz(`${dateStr} ${this.form.start_time}:00`, tz).isBefore(moment());
    },
    buildPayload() {
      const tz = this.form.timezone;
      let startStr;
      let endStr = null;

      const startDateStr = this.$moment(this.form.start_date).format('YYYY-MM-DD');

      if (this.form.allDay) {
        startStr = moment.tz(`${startDateStr} 00:00:00`, tz).utc().format('YYYY-MM-DD HH:mm:ss');

        const endDateStr = this.form.multiDay && this.form.end_date
          ? this.$moment(this.form.end_date).format('YYYY-MM-DD')
          : startDateStr;
        endStr = moment.tz(`${endDateStr} 23:59:59`, tz).utc().format('YYYY-MM-DD HH:mm:ss');
      } else {
        const start = moment.tz(`${startDateStr} ${this.form.start_time}:00`, tz);
        startStr = start.clone().utc().format('YYYY-MM-DD HH:mm:ss');

        if (this.form.multiDay && this.form.end_date) {
          const endDateStr = this.$moment(this.form.end_date).format('YYYY-MM-DD');
          const endTime = this.form.end_time || this.form.start_time;
          endStr = moment.tz(`${endDateStr} ${endTime}:00`, tz).utc().format('YYYY-MM-DD HH:mm:ss');
        } else if (Number(this.form.duration) > 0) {
          // Le formulaire saisit une durée : le modèle reste start/end,
          // on reconstruit la fin (une durée traverse minuit sans cas spécial).
          endStr = start.clone()
            .add(Number(this.form.duration), 'minutes')
            .utc()
            .format('YYYY-MM-DD HH:mm:ss');
        }
      }

      const payload = {
        title: this.form.title,
        description: this.form.description || null,
        start_date: startStr,
        end_date: endStr,
        is_all_day: this.form.allDay,
        timezone: tz,
        location_type: this.form.location_type,
        location_value: this.form.location_type ? this.form.location_value : null,
        frequency: this.form.frequency,
        status: this.form.status,
        training_uuids: this.form.training_uuids.length > 0 ? this.form.training_uuids : null,
      };

      // Only send picture if it changed (new upload URL or explicit removal)
      if (this.form.picture !== null) {
        payload.picture = this.form.picture;
      } else if (this.isEditing && this.event.picture && !this.picturePreview) {
        // Picture was removed
        payload.picture = null;
      }

      return payload;
    },
    async handle() {
      // Garde-fou : jamais d'événement dans le passé (vérif fraîche à l'envoi).
      if (this.isStartInPast()) {
        this.$buefy.toast.open({
          message: 'L\'événement ne peut pas être planifié dans le passé.',
          type: 'is-danger',
        });
        return;
      }

      // Confirm if no location
      if (!this.form.location_type) {
        const confirmed = await new Promise((resolve) => {
          this.$buefy.dialog.confirm({
            message: 'Êtes-vous sûr de ne pas ajouter de lieu à cet événement ?',
            confirmText: 'Continuer sans lieu',
            cancelText: 'Annuler',
            type: 'is-warning',
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!confirmed) return;
      }

      this.isLoading = true;
      const payload = this.buildPayload();

      try {
        if (this.isEditing) {
          await APICalendarEvents.updateCalendarEvent(this.event.uuid, payload);
          this.$buefy.toast.open({
            message: 'Événement mis à jour',
            type: 'is-success',
          });
        } else {
          await APICalendarEvents.createCalendarEvent(payload);
          this.$buefy.toast.open({
            message: 'Événement créé',
            type: 'is-success',
          });
        }
        this.$emit('saved');
        this.$emit('close');
      } catch (e) {
        this.$buefy.toast.open({
          message: 'Erreur lors de la sauvegarde',
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <form class="modal-card" style="width: 720px" novalidate @submit.prevent="dataIsValid(isLoading) && handle()">
    <header class="modal-card-head">
      <p class="modal-card-title">
        {{ isEditing ? 'Modifier l\'événement' : 'Nouvel événement' }}
      </p>
      <button type="button" class="delete" aria-label="close" @click="$emit('close')" />
    </header>

    <section class="modal-card-body">
      <!-- Title + Status -->
      <div class="columns">
        <div class="column">
          <b-field label="Titre *" label-for="event_title" v-bind="$getErrorProps($v.form.title, ['required'])">
            <b-input
              id="event_title"
              v-model.trim="form.title"
              placeholder="Nom de l'événement"
              maxlength="255"
              autofocus
            />
          </b-field>
        </div>
        <div class="column is-3">
          <b-field label="Statut" label-for="event_status">
            <b-select id="event_status" v-model="form.status" expanded>
              <option
                v-for="opt in statusOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>

      <!-- Dates -->
      <div class="mb-4">
        <div class="block">
          <b-switch v-model="form.multiDay" size="is-small">
            L'événement dure plusieurs jours
          </b-switch>
          <b-switch v-model="form.allDay" size="is-small" class="ml-3">
            L'événement dure toute une journée
          </b-switch>
        </div>

        <!-- Single day -->
        <template v-if="!form.multiDay">
          <div class="columns">
            <div class="column" :class="{ 'is-4': !form.allDay }">
              <b-field label="Date *" v-bind="$getErrorProps($v.form.start_date, ['required'])">
                <b-datepicker
                  v-model="form.start_date"
                  placeholder="Sélectionner..."
                  icon="calendar"
                  :first-day-of-week="1"
                  :min-date="minStartDate"
                />
              </b-field>
            </div>
            <template v-if="!form.allDay">
              <div class="column">
                <b-field label="Heure de début" label-for="event_start_time">
                  <b-input id="event_start_time" v-model="form.start_time" type="time" expanded @click.native="openTimePicker" />
                </b-field>
              </div>
              <div class="column">
                <b-field label="Durée (minutes)" label-for="event_duration">
                  <b-input
                    id="event_duration"
                    v-model="form.duration"
                    type="number"
                    min="1"
                    max="1440"
                    placeholder="60"
                    expanded
                  />
                </b-field>
                <p class="help">Prévisionnelle, optionnelle</p>
              </div>
            </template>
          </div>
        </template>

        <!-- Multi day -->
        <template v-else>
          <div class="columns">
            <div class="column">
              <b-field label="Date de début *" label-for="event_start_date_multi" v-bind="$getErrorProps($v.form.start_date, ['required'])">
                <b-datepicker
                  id="event_start_date_multi"
                  v-model="form.start_date"
                  placeholder="Sélectionner..."
                  icon="calendar"
                  :first-day-of-week="1"
                  :min-date="minStartDate"
                />
              </b-field>
            </div>
            <div v-if="!form.allDay" class="column w-144 is-narrow">
              <b-field label="Heure de début" label-for="event_start_time_multi">
                <b-input id="event_start_time_multi" v-model="form.start_time" type="time" @click.native="openTimePicker" />
              </b-field>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <b-field label="Date de fin *" label-for="event_end_date" v-bind="$getErrorProps($v.form.end_date, ['required'])">
                <b-datepicker
                  id="event_end_date"
                  v-model="form.end_date"
                  placeholder="Sélectionner..."
                  icon="calendar"
                  :first-day-of-week="1"
                  :min-date="form.start_date || minStartDate"
                />
              </b-field>
            </div>
            <div v-if="!form.allDay" class="column w-144 is-narrow">
              <b-field label="Heure de fin" label-for="event_end_time_multi">
                <b-input id="event_end_time_multi" v-model="form.end_time" type="time" @click.native="openTimePicker" />
              </b-field>
            </div>
          </div>
        </template>

        <p v-if="startInPast" class="help is-danger mt-2">
          {{ form.allDay
            ? 'Cette journée est déjà passée : choisissez une date à venir.'
            : 'L\'heure de début est déjà passée : choisissez une date et une heure à venir.' }}
        </p>
      </div>

      <!-- Timezone + Frequency -->
      <div class="columns">
        <div class="column is-6">
          <b-field label="Fuseau horaire *" label-for="event_timezone" v-bind="$getErrorProps($v.form.timezone, ['required'])">
            <b-select id="event_timezone" v-model="form.timezone" expanded>
              <option
                v-for="tz in timezoneOptions"
                :key="tz.value"
                :value="tz.value"
              >
                {{ tz.label }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Fréquence *" label-for="event_frequency" v-bind="$getErrorProps($v.form.frequency, ['required'])">
            <b-select id="event_frequency" v-model="form.frequency" expanded :disabled="form.multiDay">
              <option
                v-for="opt in FREQUENCY_OPTIONS"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </b-select>
          </b-field>
          <p v-if="form.multiDay" class="help">
            Indisponible pour un événement sur plusieurs jours.
          </p>
        </div>
      </div>
      <b-message v-if="dateWarning" type="is-warning" has-icon>
        {{ dateWarning }}
      </b-message>

      <!-- Location -->
      <b-field>
        <template #label>
          <span class="is-inline-flex is-align-items-center">
            Lieu
            <b-tooltip multilined position="is-right" class="ml-2">
              <b-icon class="has-text-black70" icon="question-circle" pack="far" size="is-small" />
              <template #content>
                Où se déroule l'événement : adresse, lien ou visioconférence. Affiché sur la fiche de l'événement côté apprenant ; pour une visio, un bouton « Rejoindre » y mène.
              </template>
            </b-tooltip>
          </span>
        </template>
        <b-dropdown v-model="form.location_type" aria-role="list">
          <template #trigger>
            <b-button
              :icon-left="currentLocationOption.icon"
              icon-right="chevron-down"
              type="is-light"
            >
              {{ currentLocationOption.label }}
            </b-button>
          </template>

          <b-dropdown-item :value="null" aria-role="listitem">
            <b-icon :icon="NO_LOCATION_OPTION.icon" size="is-small" class="mr-2" />
            {{ NO_LOCATION_OPTION.label }}
          </b-dropdown-item>
          <b-dropdown-item
            v-for="opt in LOCATION_OPTIONS"
            :key="opt.value"
            :value="opt.value"
            aria-role="listitem"
          >
            <b-icon :icon="opt.icon" size="is-small" class="mr-2" />
            {{ opt.label }}
          </b-dropdown-item>
        </b-dropdown>
      </b-field>
      <b-field v-if="form.location_type" v-bind="$getErrorProps($v.form.location_value, locationErrorRules)">
        <b-input
          id="event_location_value"
          v-model.trim="form.location_value"
          :placeholder="currentLocationOption.placeholder"
          maxlength="500"
          expanded
        />
      </b-field>

      <hr class="has-background-grey">

      <!-- Sections repliables -->
      <AppToggleSection class="mt-5" :is-open="openAccess" title-tag="h3" title-size="is-6" disabled-scroll>
        <template #title>
          <span class="is-inline-flex is-align-items-center">
            Accès
            <b-tooltip multilined position="is-right" class="ml-2" @click.native.stop.prevent>
              <b-icon class="has-text-black70" icon="question-circle" pack="far" size="is-small" />
              <template #content>
                Détermine quels apprenants voient l'événement dans leur calendrier. Sans formation sélectionnée, il est visible par tous vos apprenants ; sinon, uniquement par ceux inscrits aux formations choisies.
              </template>
            </b-tooltip>
          </span>
        </template>
        <template #default>
          <div>
            <b-button icon-left="book" @click="showTrainingModal = true">
              Sélectionner des formations
              <span v-if="form.training_uuids.length" class="tag is-primary ml-2">
                {{ form.training_uuids.length }}
              </span>
            </b-button>
            <div v-if="form.training_uuids.length" class="mt-3">
              <b-tag
                v-for="uuid in form.training_uuids"
                :key="uuid"
                closable
                class="mr-1 mb-1"
                @close="removeTraining(uuid)"
              >
                {{ getTrainingName(uuid) }}
              </b-tag>
            </div>
            <p class="help mt-2">
              {{ trainingHelpText }}
            </p>
          </div>
        </template>
      </AppToggleSection>

      <AppToggleSection :is-open="openDescription" title-tag="h3" title-size="is-6" disabled-scroll>
        <template #title>
          <span class="is-inline-flex is-align-items-center">
            Description
            <b-tooltip multilined position="is-right" class="ml-2" @click.native.stop.prevent>
              <b-icon class="has-text-black70" icon="question-circle" pack="far" size="is-small" />
              <template #content>
                Texte détaillé affiché sur la fiche de l'événement côté apprenant, sous le titre (programme, prérequis, consignes…).
              </template>
            </b-tooltip>
          </span>
        </template>
        <template #default>
          <b-field>
            <TiptapEditor
              :value="form.description"
              :height="180"
              :hidden="['h2']"
              placeholder="Description de l'événement"
              @input="form.description = $event"
            />
          </b-field>
        </template>
      </AppToggleSection>

      <AppToggleSection :is-open="openImage" title-tag="h3" title-size="is-6" disabled-scroll :has-sep="false">
        <template #title>
          <span class="is-inline-flex is-align-items-center">
            Image
            <b-tooltip multilined position="is-right" class="ml-2" @click.native.stop.prevent>
              <b-icon class="has-text-black70" icon="question-circle" pack="far" size="is-small" />
              <template #content>
                Visuel d'illustration affiché côté apprenant sur la carte de l'événement (liste) et sur sa fiche. Optionnel.
              </template>
            </b-tooltip>
          </span>
        </template>
        <template #default>
          <div v-if="picturePreview">
            <figure class="image" style="max-width: 200px; position: relative;">
              <img
                class="bradius-8"
                :src="picturePreview"
                alt="Aperçu"
              >
              <div v-if="isUploading" class="has-text-centered" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.7); border-radius: 8px;">
                <b-icon icon="spinner" custom-class="fa-spin" />
              </div>
            </figure>
            <p class="mt-3">
              <b-button type="is-text" class="has-text-danger" :disabled="isUploading" @click="removePicture">
                Supprimer l'image
              </b-button>
            </p>
          </div>
          <div v-else class="mw-260">
            <b-upload v-model="pictureFile" accept="image/*" expanded @input="handlePicture">
              <b-button tag="a" class="button" icon-left="file-image" outlined expanded>
                Sélectionner une image
              </b-button>
            </b-upload>
            <p class="help">Format carré, max 5 Mo</p>
          </div>
        </template>
      </AppToggleSection>
    </section>

    <footer class="modal-card-foot is-justify-content-flex-end">
      <div class="buttons">
        <b-button type="is-text" @click="$emit('close')">
          Annuler
        </b-button>
        <b-button
          type="is-primary"
          native-type="submit"
          :loading="isLoading"
          :disabled="isUploading || startInPast"
        >
          {{ isEditing ? 'Modifier' : 'Créer' }}
        </b-button>
      </div>
    </footer>

    <!-- Training selection modal : même composant que l'import d'apprenants
         (classes virtuelles) et l'import de membres (communauté). -->
    <b-modal v-model="showTrainingModal" has-modal-card :destroy-on-hide="true">
      <CommunityMembersImportForm
        class="mw-640"
        :import-fn="applyTrainingSelection"
        :initial-trainings="form.training_uuids"
        :labels="{
          import_members: 'Sélectionner des formations',
          add_all_learners: 'Les apprenants des formations sélectionnées verront cet événement',
          import: 'Valider',
        }"
        @done="showTrainingModal = false"
        @cancel="showTrainingModal = false"
      />
    </b-modal>
  </form>
</template>

<style lang="scss" scoped>
// Le clic ouvre le sélecteur natif (showPicker) : le curseur main
// signale que tout le champ est cliquable, comme le champ date.
::v-deep input[type='time'] {
  cursor: pointer;
}
</style>
